import { io } from 'socket.io-client';
import { ref } from 'vue';
import { ApiEventNames } from '../shared/api-contract';
import type { Order } from '../shared/types';
import { API_BASE_URL } from '../config/runtime';
import { getDesktopSessionCredentials } from './apiClient';

let socket: ReturnType<typeof io> | null = null;

// Live connection state for screens that want to show it (used by the v2 interface).
export const realtimeStatus = ref<'connecting' | 'online' | 'offline'>('connecting');

/* eslint-disable no-unused-vars */
export interface OrdersRealtimeStore {
  orders: Order[];
  pushOrder: (_order: Order) => void;
  replaceOrder: (_order: Order) => void;
  loadOrders: () => Promise<void>;
}
/* eslint-enable no-unused-vars */

export const connectRealtime = (store: OrdersRealtimeStore) => {
  if (socket) {
    return socket;
  }

  socket = io(API_BASE_URL, {
    transports: ['websocket', 'polling'],
  });

  // A dropped connection means any order created/updated while offline was
  // never received (server only broadcasts to currently-connected sockets).
  // Reload the full list on every (re)connect so a network blip can't leave
  // a device stuck showing stale orders indefinitely.
  socket.on('connect', () => {
    console.log('Realtime connected', socket?.id);
    identifyRealtime();
    realtimeStatus.value = 'online';
    void store.loadOrders();
  });

  socket.on(ApiEventNames.orderCreated, (payload: { order: Order }) => {
    if (!store.orders.find((order) => order.id === payload.order.id)) {
      store.pushOrder(payload.order);
    }
  });

  socket.on(ApiEventNames.orderUpdated, (payload: { order: Order }) => {
    store.replaceOrder(payload.order);
  });

  socket.on('disconnect', () => {
    console.log('Realtime disconnected');
    realtimeStatus.value = 'offline';
  });

  socket.on('connect_error', () => {
    realtimeStatus.value = 'offline';
  });

  return socket;
};

// Order events go only to signed-in staff devices. Called on connect and after
// every navigation, so a sign-in on the start screen joins the feed at once.
export function identifyRealtime() {
  if (!socket || !socket.connected) return;
  const { userId, loginId } = getDesktopSessionCredentials();
  if (userId && loginId) socket.emit('identify', { userId, loginId });
}
